/**
 * @fileoverview Business Logic (BL) for the Farmeando Aura game dynamic.
 * Encapsulates validations, parameter processing, and delegates calls to the Data Layer (DL).
 * 
 * @module utilities/BL/FarmeandoAura
 */

import * as DataLayerFarmeandoAura from './../DL/FarmeandoAura';

/**
 * Retrieves all visual themes configured for Farmeando Aura.
 * 
 * @async
 * @function GetThemes
 * @returns {Promise<{success: boolean, data?: Array<Object>, message?: string}>} Response payload with theme list.
 */
export const GetThemes = async () => {
    return await DataLayerFarmeandoAura.GetThemes();
};

/**
 * Retrieves a single theme by ID.
 * 
 * @async
 * @function GetTheme
 * @param {number} themeIdentifier - Unique theme ID.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Response payload with theme data.
 */
export const GetTheme = async (themeIdentifier) => {
    if (!themeIdentifier || Number(themeIdentifier) <= 0) {
        return { success: false, message: 'El identificador del tema es inválido.' };
    }
    return await DataLayerFarmeandoAura.GetTheme(themeIdentifier);
};

/**
 * Validates and saves a theme configuration.
 * 
 * @async
 * @function SaveTheme
 * @param {Object} themeData - Theme attributes and configuration.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Response payload with saved theme.
 */
export const SaveTheme = async (themeData) => {
    if (!themeData || typeof themeData !== 'object') {
        return { success: false, message: 'La información del tema es obligatoria.' };
    }

    if (!themeData.name || themeData.name.trim() === '') {
        return { success: false, message: 'El nombre del tema es obligatorio.' };
    }

    return await DataLayerFarmeandoAura.SaveTheme(themeData);
};

/**
 * Deletes a theme by ID after validating its presence.
 * 
 * @async
 * @function DeleteTheme
 * @param {number} themeIdentifier - Unique theme ID.
 * @returns {Promise<{success: boolean, message?: string}>} Response payload confirming deletion.
 */
export const DeleteTheme = async (themeIdentifier) => {
    if (!themeIdentifier || Number(themeIdentifier) <= 0) {
        return { success: false, message: 'El identificador del tema a eliminar es inválido.' };
    }
    return await DataLayerFarmeandoAura.DeleteTheme(themeIdentifier);
};

/**
 * Clones an existing theme record.
 * 
 * @async
 * @function CloneTheme
 * @param {number} themeIdentifier - Source theme ID.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Response payload with cloned theme.
 */
export const CloneTheme = async (themeIdentifier) => {
    if (!themeIdentifier || Number(themeIdentifier) <= 0) {
        return { success: false, message: 'El identificador del tema a clonar es inválido.' };
    }
    return await DataLayerFarmeandoAura.CloneTheme(themeIdentifier);
};

/**
 * Retrieves the global room configuration.
 * 
 * @async
 * @function GetConfig
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Response payload with configuration.
 */
export const GetConfig = async () => {
    return await DataLayerFarmeandoAura.GetConfig();
};

/**
 * Saves the global room configuration.
 * 
 * @async
 * @function SaveConfig
 * @param {Object} configurationData - Room configuration data.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Response payload with saved configuration.
 */
export const SaveConfig = async (configurationData) => {
    if (!configurationData || typeof configurationData !== 'object') {
        return { success: false, message: 'Los datos de configuración son obligatorios.' };
    }
    return await DataLayerFarmeandoAura.SaveConfig(configurationData);
};

/**
 * Retrieves the log of past matches.
 * 
 * @async
 * @function GetMatchesHistory
 * @param {number} [queryLimit=50] - Number of matches to fetch.
 * @returns {Promise<{success: boolean, data?: Array<Object>, message?: string}>} Response payload with match history.
 */
export const GetMatchesHistory = async (queryLimit = 50) => {
    return await DataLayerFarmeandoAura.GetMatchesHistory(queryLimit);
};

/**
 * Packs a single file into FormData and uploads it to the backend.
 * 
 * @async
 * @function UploadMedia
 * @param {File|Blob} fileObject - Media file to upload.
 * @param {string} [mediaCategory=''] - Optional category (avatar, deck, background, logo).
 * @returns {Promise<{success: boolean, url?: string, message?: string}>} Upload result with file URL.
 */
export const UploadMedia = async (fileObject, mediaCategory = '') => {
    if (!fileObject) {
        return { success: false, message: 'No se ha seleccionado ningún archivo.' };
    }

    const multipartFormData = new FormData();
    multipartFormData.append('file', fileObject);
    if (mediaCategory) {
        multipartFormData.append('type', mediaCategory);
    }

    return await DataLayerFarmeandoAura.UploadMedia(multipartFormData);
};

/**
 * Packs an array of files into FormData and uploads them in batch.
 * 
 * @async
 * @function UploadBatchMedia
 * @param {Array<File|Blob>|FileList} filesCollection - Array or FileList of media files.
 * @param {string} [mediaCategory='deck'] - Category (default: 'deck').
 * @returns {Promise<{success: boolean, urls?: Array<string>, count?: number, message?: string}>} Batch result.
 */
export const UploadBatchMedia = async (filesCollection, mediaCategory = 'deck') => {
    if (!filesCollection || filesCollection.length === 0) {
        return { success: false, message: 'No se han proporcionado archivos para la carga múltiple.' };
    }

    const multipartFormData = new FormData();
    for (let fileIndex = 0; fileIndex < filesCollection.length; fileIndex++) {
        multipartFormData.append('files', filesCollection[fileIndex]);
    }
    if (mediaCategory) {
        multipartFormData.append('type', mediaCategory);
    }

    return await DataLayerFarmeandoAura.UploadBatchMedia(multipartFormData);
};

/**
 * Displays the clean promotional cover screen on the stadium overlay.
 * 
 * @async
 * @function ShowCover
 * @returns {Promise<{success: boolean, message?: string, state?: Object}>} Response payload.
 */
export const ShowCover = async () => {
    return await DataLayerFarmeandoAura.ShowCover();
};

/**
 * Displays the interactive combat HUD screen on the stadium overlay.
 * 
 * @async
 * @function ShowGame
 * @returns {Promise<{success: boolean, message?: string, state?: Object}>} Response payload.
 */
export const ShowGame = async () => {
    return await DataLayerFarmeandoAura.ShowGame();
};

/**
 * Starts a new combat round with specified duration, game mode, and visual animation settings.
 * 
 * @async
 * @function StartGame
 * @param {number} [durationSeconds=10] - Round duration in seconds.
 * @param {string} [gameMode='score'] - Game mode ('score', 'health_ko', 'health_timed').
 * @param {Object} [options={}] - Additional visual options.
 * @returns {Promise<{success: boolean, message?: string, state?: Object}>} Response payload.
 */
export const StartGame = async (durationSeconds = 10, gameMode = 'score', options = {}) => {
    const sanitizedDuration = Number(durationSeconds) > 0 ? Number(durationSeconds) : 10;
    const sanitizedMode = ['score', 'health_ko', 'health_timed'].includes(gameMode) ? gameMode : 'score';

    return await DataLayerFarmeandoAura.StartGame({
        durationSeconds: sanitizedDuration,
        gameMode: sanitizedMode,
        showComboPopups: options.showComboPopups !== undefined ? !!options.showComboPopups : true,
        showDeckTransforms: options.showDeckTransforms !== undefined ? !!options.showDeckTransforms : true,
        deckTransformInterval: Number(options.deckTransformInterval) > 0 ? Number(options.deckTransformInterval) : 15
    });
};

/**
 * Pauses the active combat round.
 * 
 * @async
 * @function PauseGame
 * @returns {Promise<{success: boolean, message?: string, state?: Object}>} Response payload.
 */
export const PauseGame = async () => {
    return await DataLayerFarmeandoAura.PauseGame();
};

/**
 * Resumes a paused combat round.
 * 
 * @async
 * @function ResumeGame
 * @returns {Promise<{success: boolean, message?: string, state?: Object}>} Response payload.
 */
export const ResumeGame = async () => {
    return await DataLayerFarmeandoAura.ResumeGame();
};

/**
 * Triggers an emergency stop and ends the game immediately.
 * 
 * @async
 * @function FinishEarly
 * @returns {Promise<{success: boolean, message?: string, state?: Object}>} Response payload.
 */
export const FinishEarly = async () => {
    return await DataLayerFarmeandoAura.FinishEarly();
};

/**
 * Resets the game state back to idle.
 * 
 * @async
 * @function ResetGame
 * @returns {Promise<{success: boolean, message?: string, state?: Object}>} Response payload.
 */
export const ResetGame = async () => {
    return await DataLayerFarmeandoAura.ResetGame();
};

/**
 * Retrieves the current real-time state from memory.
 * 
 * @async
 * @function GetRealtimeState
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Response payload with state data.
 */
export const GetRealtimeState = async () => {
    return await DataLayerFarmeandoAura.GetRealtimeState();
};
